import React from 'react';
import { useQuery } from 'react-query';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  People,
  Class,
  Assignment,
  TrendingUp,
  School,
  Group,
  Schedule,
  Assessment,
} from '@mui/icons-material';
import { allocationsAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const navigate = useNavigate();
  
  // Check authentication status
  const token = localStorage.getItem('token');
  console.log('🔐 Dashboard: Authentication check:', { 
    hasToken: !!token, 
    tokenLength: token ? token.length : 0,
    tokenPreview: token ? token.substring(0, 20) + '...' : 'No token'
  });

  // Fetch dashboard stats
  const { data: stats, isLoading, error } = useQuery(
    'dashboardStats',
    async () => {
      console.log('🔍 Dashboard: Making API call to getStats()');
      try {
        const result = await allocationsAPI.getStats();
        console.log('✅ Dashboard: API call successful:', result);
        return result;
      } catch (err) {
        console.error('❌ Dashboard: API call failed:', err);
        throw err;
      }
    },
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Debug logging
  console.log('Dashboard API Response:', { stats, isLoading, error });
  console.log('Stats object:', stats);
  console.log('Stats.data:', stats?.data);
  console.log('Stats.data.stats:', stats?.data?.stats);

  const dashboardData = (stats?.data?.stats || stats?.data || stats) || {
    totalStudents: 0,
    assignedStudents: 0,
    unassignedStudents: 0,
    totalBatches: 0,
    activeBatches: 0,
    inclusiveBatches: 0,
    specialNeedsStudents: 0,
    averageMatchScore: 0,
    assignmentRate: 0,
  };

  console.log('Dashboard Data Object:', dashboardData);

  const StatCard = ({ title, value, icon, color, subtitle }) => {
    console.log('StatCard render:', { title, value, icon, color, subtitle });
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                backgroundColor: color,
                borderRadius: '50%',
                p: 1,
                mr: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="h4" component="div" fontWeight="bold">
                {value !== undefined && value !== null ? value : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>
          </Box>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Remove the error alert that was preventing rendering
  // if (error) {
  //   return (
  //     <Alert severity="error" sx={{ mb: 2 }}>
  //       Failed to load dashboard data. Please try again.
  //     </Alert>
  //   );
  // }

  // If there's an error, show a message but still display the cards with 0 values
  if (error) {
    console.error('Dashboard Error:', error);
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome to BacchaPanti. Here's an overview of your system.
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Students"
            value={dashboardData.totalStudents}
            icon={<People sx={{ color: 'white' }} />}
            color="#1976d2"
            subtitle="Active · registered students"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Assigned Students"
            value={dashboardData.assignedStudents}
            icon={<Assignment sx={{ color: 'white' }} />}
            color="#2e7d32"
            subtitle={`${((dashboardData.assignmentRate || 0) * 100).toFixed(1)}% assignment rate`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Live Batches"
            value={dashboardData.activeBatches}
            icon={<Class sx={{ color: 'white' }} />}
            color="#ed6c02"
            subtitle="To start or in progress"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Average Match Score"
            value={`${((dashboardData.averageMatchScore || 0) * 100).toFixed(1)}%`}
            icon={<TrendingUp sx={{ color: 'white' }} />}
            color="#9c27b0"
            subtitle="Quality indicator"
          />
        </Grid>
      </Grid>

      {/* Additional Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Unassigned Students"
            value={dashboardData.unassignedStudents}
            icon={<School sx={{ color: 'white' }} />}
            color="#d32f2f"
            subtitle="Need allocation"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Inclusive Batches"
            value={dashboardData.inclusiveBatches}
            icon={<Group sx={{ color: 'white' }} />}
            color="#1565c0"
            subtitle="Special needs support"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Special Needs Students"
            value={dashboardData.specialNeedsStudents}
            icon={<Assessment sx={{ color: 'white' }} />}
            color="#7b1fa2"
            subtitle="Require special attention"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Batches"
            value={dashboardData.totalBatches}
            icon={<Schedule sx={{ color: 'white' }} />}
            color="#388e3c"
            subtitle="Non-inactive batches"
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer', '&:hover': { elevation: 4 } }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Assignment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Assign Students</Typography>
                <Typography variant="body2" color="text.secondary">
                  Manually assign students to batches
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer', '&:hover': { elevation: 4 } }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Class sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Create Batch</Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a new batch
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer', '&:hover': { elevation: 4 } }} onClick={() => navigate('/students?add=1')}>
              <CardContent sx={{ textAlign: 'center' }}>
                <People sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">Add Student</Typography>
                <Typography variant="body2" color="text.secondary">
                  Register a new student
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer', '&:hover': { elevation: 4 } }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Assessment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6">View Reports</Typography>
                <Typography variant="body2" color="text.secondary">
                  Access detailed reports
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Recent Activity */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Activity
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No recent activity to display.
        </Typography>
      </Paper>
    </Box>
  );
};

export default Dashboard; 